import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Heart, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LS_KEY = 'zenscroll_rating_survey_done';
const SHOW_DELAY_MS = 9000;
const FEEDBACK_MAX = 750;

const APP_STORE_URL = 'https://apps.apple.com/us/app/zenscroll/id6749971356?action=write-review';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.zenscroll.mobile';

function storeUrl() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;
  return /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent) ? APP_STORE_URL : PLAY_STORE_URL;
}

/**
 * One-time in-app satisfaction survey.
 * Shows only after real engagement (3+ logins OR first content created —
 * decided server-side by GET /rating-survey/status), only once per user.
 * 4-5 stars → store review CTA. 1-3 stars → private feedback form.
 */
export default function RatingSurveyModal({ token }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [step, setStep] = useState('rate'); // rate | thanks | feedback
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const sendEvent = (payload) =>
    axios.post(`${API}/rating-survey/event`, { platform: Capacitor.getPlatform(), ...payload }, { headers }).catch(() => {});

  useEffect(() => {
    if (!token) { setOpen(false); return undefined; }
    if (localStorage.getItem(LS_KEY) === 'true') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/rating-survey/status`, { headers });
        if (cancelled) return;
        if (data?.reason === 'done') {
          localStorage.setItem(LS_KEY, 'true');
          return;
        }
        if (data?.show) {
          timerRef.current = setTimeout(() => {
            // Don't stack on top of other global modals.
            if (document.querySelector('[data-testid="heard-about-survey"], [data-testid="onboarding-tutorial"], [data-testid="onboarding-overlay"]')) return;
            setOpen(true);
            sendEvent({ action: 'prompted' });
          }, SHOW_DELAY_MS);
        }
      } catch { /* soft-fail — never block the app */ }
    })();
    return () => { cancelled = true; clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const finish = (action) => {
    localStorage.setItem(LS_KEY, 'true');
    setOpen(false);
    if (action) sendEvent({ action });
  };

  const pickRating = (value) => {
    setRating(value);
    sendEvent({ action: 'rated', rating: value });
    setTimeout(() => setStep(value >= 4 ? 'thanks' : 'feedback'), 350);
  };

  const openStore = () => {
    sendEvent({ action: 'store_opened' });
    localStorage.setItem(LS_KEY, 'true');
    window.open(storeUrl(), '_blank', 'noopener');
    setOpen(false);
  };

  const submitFeedback = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/rating-survey/event`,
        { action: 'feedback', rating, feedback: feedback.trim().slice(0, FEEDBACK_MAX), platform: Capacitor.getPlatform() },
        { headers }
      );
      toast.success('Thank you — your feedback helps ZenScroll grow.');
      localStorage.setItem(LS_KEY, 'true');
      setOpen(false);
    } catch {
      toast.error('Could not send feedback — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="rating-survey-modal"
          className="fixed inset-0 z-[9998] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-[#06070A]/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            role="dialog"
            aria-labelledby="rating-survey-title"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-md rounded-3xl border border-[#1C4749]/40 bg-gradient-to-b from-[#101F1D] to-[#0A1514] shadow-[0_30px_60px_rgba(0,0,0,0.5)] p-6 sm:p-8 text-center"
          >
            <button
              type="button"
              onClick={() => finish('dismissed')}
              data-testid="rating-survey-close-btn"
              aria-label="Close survey"
              className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors p-1 rounded-full"
            >
              <X className="w-4 h-4" />
            </button>

            {step === 'rate' && (
              <>
                <div className="mx-auto inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1C4749]/20 border border-[#1C4749]/40 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5EEAD4]">
                  <Heart className="w-3 h-3" /> A quick moment
                </div>
                <h2 id="rating-survey-title" className="mt-4 text-2xl sm:text-3xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Enjoying ZenScroll?
                </h2>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">
                  We&rsquo;re building a calmer, more inspiring social experience and would love your feedback.
                </p>
                <p className="mt-5 text-xs uppercase tracking-[0.18em] text-white/50">How would you rate your experience?</p>
                <div className="mt-3 flex items-center justify-center gap-2" data-testid="rating-star-row">
                  {[1, 2, 3, 4, 5].map((v) => {
                    const filled = v <= (hovered || rating);
                    return (
                      <motion.button
                        key={v}
                        type="button"
                        data-testid={`rating-star-${v}`}
                        aria-label={`${v} star${v > 1 ? 's' : ''}`}
                        whileTap={{ scale: 0.85 }}
                        whileHover={{ scale: 1.15 }}
                        onMouseEnter={() => setHovered(v)}
                        onMouseLeave={() => setHovered(0)}
                        onClick={() => pickRating(v)}
                        className="p-1.5"
                      >
                        <Star
                          className={`w-8 h-8 transition-colors ${filled ? 'text-[#FFD166] fill-[#FFD166]' : 'text-white/25'}`}
                        />
                      </motion.button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  data-testid="rating-maybe-later-btn"
                  onClick={() => finish('dismissed')}
                  className="mt-6 text-xs text-white/45 hover:text-white/80 transition-colors underline-offset-2 hover:underline"
                >
                  Maybe later
                </button>
              </>
            )}

            {step === 'thanks' && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="rating-thanks-step">
                <div className="mx-auto flex items-center justify-center gap-1.5 mt-2">
                  {Array.from({ length: rating }).map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-[#FFD166] fill-[#FFD166]" />
                  ))}
                </div>
                <h2 className="mt-4 text-2xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Thank you!
                </h2>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">
                  Your support helps ZenScroll grow and reach more people looking for a healthier social experience.
                </p>
                <button
                  type="button"
                  data-testid="rating-leave-review-btn"
                  onClick={openStore}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-6 py-3 hover:bg-[#2A6F72] transition-colors shadow-[0_0_18px_rgba(28,71,73,0.45)]"
                >
                  Leave a Review
                </button>
                <button
                  type="button"
                  data-testid="rating-thanks-later-btn"
                  onClick={() => finish('dismissed')}
                  className="mt-3 text-xs text-white/45 hover:text-white/80 transition-colors underline-offset-2 hover:underline"
                >
                  Not now
                </button>
              </motion.div>
            )}

            {step === 'feedback' && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="rating-feedback-step" className="text-left">
                <h2 className="mt-1 text-xl text-white text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Thank you for your honest feedback
                </h2>
                <p className="mt-2 text-sm text-white/70 text-center leading-relaxed">
                  We&rsquo;d love to know how we can improve.
                </p>
                <textarea
                  data-testid="rating-feedback-input"
                  value={feedback}
                  maxLength={FEEDBACK_MAX}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Tell us what we could improve..."
                  rows={4}
                  className="mt-4 w-full rounded-2xl bg-white/5 border border-white/15 text-white text-sm placeholder:text-white/40 p-4 focus:outline-none focus:border-[#5EEAD4]/60 resize-none"
                />
                <div className="mt-1 text-right text-[10px] text-white/40 tabular-nums">{feedback.length}/{FEEDBACK_MAX}</div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    data-testid="rating-feedback-later-btn"
                    onClick={() => finish('dismissed')}
                    className="text-sm text-white/55 hover:text-white transition-colors underline-offset-2 hover:underline"
                  >
                    Maybe Later
                  </button>
                  <button
                    type="button"
                    data-testid="rating-feedback-submit-btn"
                    disabled={submitting}
                    onClick={submitFeedback}
                    className="inline-flex items-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-6 py-2.5 hover:bg-[#2A6F72] transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Sending…' : 'Submit Feedback'} {!submitting && <Send className="w-4 h-4" />}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
